import { FC, useCallback, useMemo, useState } from 'react';
import { Box, Modal, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

import CheckedMarkIcon from '../../assets/icons/Checkmark.png';
import { IPlan } from '../Plans/Plans';

import styles from './PlanCard.module.scss';
import { useAppSelector } from '../../hooks/redux';
import { useCancelSubscriptionMutation } from '../../store/services/customizeOptionsApi';
import CustomButton from '../../common/CustomButton';
import { useCurrencySymbol } from '../../hooks/useCurrencySymbol';
import { hasActiveReferralSubscription } from '../../utils/planPricing';

type PlanCardProps = {
  plan: IPlan;
};

const formatPremiumExpiration = (iso: string | null | undefined) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const PremiumPlanCard: FC<PlanCardProps> = ({ plan }) => {
  const user = useAppSelector(state => state.user);
  const planSlice = useAppSelector(state => state.plan);
  const currencySymbol = useCurrencySymbol();
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);
  const [cancelSubscription, { isLoading: isCancelling }] = useCancelSubscriptionMutation();

  const expirationIso = plan.ends_at ?? planSlice.ends_at;
  const expirationLabel = useMemo(() => formatPremiumExpiration(expirationIso), [expirationIso]);
  const referralDiscount = plan.referral_discount;
  const showReferralJoinedNotice = hasActiveReferralSubscription(referralDiscount);

  const subscriptionStatusLabel = user.is_premium ? 'Active (Premium)' : 'Inactive';

  const openCancelModal = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setCancelError(null);
    setCancelModalOpen(true);
  }, []);

  const closeCancelModal = useCallback(() => {
    if (isCancelling) return;
    setCancelModalOpen(false);
    setCancelError(null);
  }, [isCancelling]);

  const onConfirmCancelSubscription = useCallback(async () => {
    setCancelError(null);
    try {
      await cancelSubscription().unwrap();
      setCancelModalOpen(false);
    } catch {
      setCancelError('Could not cancel subscription. Please try again.');
    }
  }, [cancelSubscription]);

  return (
    <>
      <Box className={`${styles.subscriptionPlan} ${styles.premium}`}>
        <Box className={styles.mainInfo}>
          <Box
            display="flex"
            flexDirection="column"
          >
            <Box className={styles.titleWrapper}>
              <Typography
                variant="h4Bold"
                className={styles.title}
              >
                {plan.type}
              </Typography>
              {plan.id === user.chosen_plan && (
                <Typography
                  variant="body1"
                  className={styles.current}
                >
                  Current Plan
                </Typography>
              )}
            </Box>
            <Typography
              variant="h11"
              className={styles.subtitle}
            >
              For globetrotters seeking premium level AI assistance to plan and explore using the app’s true power
            </Typography>
            <Typography
              variant="h6Bold"
              className={styles.payment}
            >
              {currencySymbol}
              {Math.floor(plan.price_in_cents / 100)}
              <sup style={{ fontSize: '0.6em', marginLeft: '1px' }}>
                {(plan.price_in_cents % 100).toString().padStart(2, '0')}
              </sup>
              <Typography
                variant="h13"
                component="span"
                sx={{ marginLeft: '4px' }}
              >
                /year
              </Typography>
            </Typography>
            <Typography className={styles.nextBilling}>
              {user.premium_canceled_at_period_end ? 'Canceled (Pending Expiration):' : 'Next billing:'}{' '}
              {expirationLabel}
            </Typography>
            {showReferralJoinedNotice && referralDiscount ? (
              <Typography
                variant="h11"
                className={styles.referralJoinedNotice}
              >
                You joined via the referral program with a {referralDiscount.discount_percent}% discount.
              </Typography>
            ) : null}
          </Box>
          {!user.premium_canceled_at_period_end && (
            <Box
              className={styles.termsWrapper}
              onClick={openCancelModal}
            >
              <Typography
                variant="h10Bold"
                className={styles.terms}
              >
                Cancel subscription
              </Typography>
            </Box>
          )}
        </Box>
        <Box className={styles.benefitsGroup}>
          {plan.benefits.map((benefit, index) => (
            <Box
              key={index}
              className={styles.benefitWrapper}
            >
              <img
                src={CheckedMarkIcon}
                className={styles.benefitIcon}
                alt="checked-mark"
              />
              <Typography
                variant="h12"
                className={styles.benefit}
              >
                {benefit}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>

      <Modal
        open={cancelModalOpen}
        onClose={(_, reason) => {
          if (reason === 'backdropClick' && isCancelling) return;
          closeCancelModal();
        }}
        aria-labelledby="cancel-subscription-title"
        aria-describedby="cancel-subscription-description"
      >
        <Box className={styles.cancelModal}>
          <Box className={styles.cancelModalClose}>
            <CloseIcon
              onClick={closeCancelModal}
              aria-label="Close"
              sx={{ cursor: isCancelling ? 'default' : 'pointer', opacity: isCancelling ? 0.5 : 1 }}
            />
          </Box>
          <Typography
            id="cancel-subscription-title"
            variant="h5Bold"
          >
            Cancel subscription?
          </Typography>
          <Box
            id="cancel-subscription-description"
            display="flex"
            flexDirection="column"
            gap={1.5}
          >
            <Typography
              variant="h10"
              sx={{ color: '#222B3A' }}
            >
              <Typography
                component="span"
                variant="h10Bold"
                marginRight={1}
              >
                Current subscription status:{' '}
              </Typography>
              {subscriptionStatusLabel}
            </Typography>
            <Typography
              variant="h11"
              sx={{ color: '#222B3A' }}
            >
              Your subscription will be canceled. You will retain Premium access until {expirationLabel}.
            </Typography>
            {cancelError && (
              <Typography
                variant="h11"
                color="error"
              >
                {cancelError}
              </Typography>
            )}
          </Box>
          <CustomButton
            variant="outlined"
            disabled={isCancelling}
            onClick={onConfirmCancelSubscription}
            customClass={styles.cancelButton}
          >
            Cancel subscription
          </CustomButton>
        </Box>
      </Modal>
    </>
  );
};

export default PremiumPlanCard;
